import PropTypes from 'prop-types';
import React, { cloneElement, PureComponent } from 'react';
import { createPortal } from 'react-dom';

import cls from 'classnames';

import addDays from 'date-fns/addDays';
import startOfToday from 'date-fns/startOfToday';
import get from 'lodash/get';
import isDate from 'lodash/isDate';
import noop from 'lodash/noop';

import Button from '@ott/button';
import ChevronDownIcon from '@ott/icon-chevron-down';
import CloserIcon from '@ott/icon-close';
import MoonIcon from '@ott/icon-moon';
import InputTrigger from '@ott/input-trigger';
import { l10n, lang } from '@ott/l10n';
import { withLayoutContext } from '@ott/layout-context';
import Modal from '@ott/modal';
import Overlay from '@ott/overlay';
import KM from '@ott/utility-km';

import Calendar from './blocks/Calendar/Calendar';
import DatesFormatter from './blocks/DatesFormatter/DatesFormatter';
import Heatmap from './blocks/Heatmap/Heatmap';
import {
  MAX_DAYS_OFFSET,
  OFFSET_FROM_SIDE_BORDER,
  OFFSET_FROM_SCREEN_BOTTOM_BORDER,
} from './constants';

import superagent from 'superagent';

import styles from './Datepicker.scss';

const mainLocator = 'Datepicker';

@withLayoutContext
class Datepicker extends PureComponent {
  static propTypes = {
    input: PropTypes.shape({
      value: PropTypes.object.isRequired,
      onFocus: PropTypes.func,
      onBlur: PropTypes.func,
    }).isRequired,
    meta: PropTypes.object.isRequired,
    childrenClassNames: PropTypes.shape({
      container: PropTypes.string,
      label: PropTypes.string,
      input: PropTypes.string,
    }),
    overlayProps: PropTypes.object,

    className: PropTypes.string,
    labelClass: PropTypes.string,
    withArrowIcon: PropTypes.bool,
    isResettable: PropTypes.bool,

    minDate: PropTypes.instanceOf(Date),
    maxDate: PropTypes.instanceOf(Date),
    maxDays: PropTypes.number, // максимальное кол-во дней в диапазоне.

    maxDaysTooltipText: PropTypes.string,

    endOnlyAfterStart: PropTypes.bool, // разрешает дату окончания выбрать только, если она после
    // даты начала
    pickOneDate: PropTypes.bool,
    /** Полное название месяца для pickOneDate */
    forceLongMonth: PropTypes.bool,
    differentDates: PropTypes.bool, // startDate & endDate должны отличаться
    showNights: PropTypes.bool,
    shouldShowTomorrow: PropTypes.bool,
    triggerAdditionalInfo: PropTypes.string, // например, для вывода "±2" в Турах
    foldSameDate: PropTypes.bool,
    endDateIsOptional: PropTypes.bool,
    title: PropTypes.string,
    placeholder: PropTypes.node,
    startDatePlaceholder: PropTypes.string,
    endDatePlaceholder: PropTypes.string, // если выбрана только стартовая дата и нет ховера по
    // конечной

    calendarPlacement: PropTypes.oneOf(['left', 'right']),
    errorPlacement: PropTypes.oneOf(['top', 'bottom']),

    heatmap: PropTypes.shape({
      currency: PropTypes.string.isRequired,
      min: PropTypes.number.isRequired,
      mid: PropTypes.number.isRequired,
      max: PropTypes.number.isRequired,
      time: PropTypes.number.isRequired,
      prices: PropTypes.PropTypes.object.isRequired,
    }),
    heatmapStatus: PropTypes.shape({
      initial: PropTypes.bool.isRequired,
      loading: PropTypes.bool.isRequired,
      success: PropTypes.bool.isRequired,
      error: PropTypes.bool.isRequired,
      cancelled: PropTypes.bool.isRequired,
    }),
    updateHeatmap: PropTypes.func,

    checkIfDateIsDisabled: PropTypes.func,

    timeField: PropTypes.shape({
      input: PropTypes.object,
      meta: PropTypes.object,
    }),
    defaultTimeValue: PropTypes.string,

    scrollToWeek: PropTypes.bool,
    isDisabled: PropTypes.bool, // todo проверить реализацию
    isMobileSupport: PropTypes.bool, // todo проверить реализацию
    onDateSelect: PropTypes.func,
    onTimeSelect: PropTypes.func,
    onReset: PropTypes.func,

    hideMonthsNavigation: PropTypes.bool,
    hideYearInDatesContainer: PropTypes.bool,
    usePortalOverlay: PropTypes.bool,

    closeAfterSelect: PropTypes.bool,

    CustomDatesFormatter: PropTypes.element,

    onBlur: PropTypes.func,
    onFocus: PropTypes.func,

    onOpen: PropTypes.func,
    onClose: PropTypes.func,

    startDateIsFixed: PropTypes.bool,

    specificLocators: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),

    TriggerComponent: PropTypes.elementType,
    triggerProps: PropTypes.object,
    skipMouseDownHandler: PropTypes.bool,

    defaultCalendarOpened: PropTypes.bool,
    withHeatmap: PropTypes.bool,
    showHeatmapPrices: PropTypes.bool, // отображение цен в календаре, работает, если withHeatmap == true
    showHeatmapLegend: PropTypes.bool, // легенда для heatmap, работает, если withHeatmap == true
    preventCloseCalendarOnOutsideClick: PropTypes.bool, //оставлять открытым календарь при кликах вне контейнера, если не выбраны даты
    //preventCloseCalendarOnOutsideClick выпиливаем или оставляем после завершения теста SEO-8317
  };

  static defaultProps = {
    onDateSelect: noop,
    onTimeSelect: noop,
    calendarPlacement: 'left',
    updateHeatmap: noop,
    minDate: startOfToday(),
    maxDate: addDays(startOfToday(), MAX_DAYS_OFFSET),
    hideMonthsNavigation: false,
    closeAfterSelect: false,
    hideYearInDatesContainer: false,
    usePortalOverlay: false,
    isResettable: false,
    onBlur: noop,
    onFocus: noop,
    onClose: noop,
    onOpen: noop,
    onReset: noop,
    specificLocators: [],
    childrenClassNames: {
      container: '',
      label: '',
      input: '',
    },
    maxDaysTooltipText: '',
    title: l10n('datepicker.title'),
    withArrowIcon: true,
    overlayProps: {},
    TriggerComponent: InputTrigger,
    triggerProps: {},
    startDateIsFixed: false,
    skipMouseDownHandler: false,
    forceLongMonth: false,
    shouldShowTomorrow: false,
    showHeatmapPrices: false,
    showHeatmapLegend: true,
    preventCloseCalendarOnOutsideClick: false,
    defaultCalendarOpened: false,
  };

  constructor(props, ctx) {
    super(props, ctx);

    this.state = {
      isOpen: false,
      isDirectOnly: false,
      holidays: undefined,
    };

    this.containerRef = React.createRef();
    this.overlayRef = React.createRef();
  }

  componentWillUpdate(nextProps, nextState) {
    const currentDates = get(this.props, 'input.value', {});
    const nextDates = get(nextProps, 'input.value', {});

    const { pickOneDate, updateHeatmap, withHeatmap } = this.props;
    const { isOpen, isDirectOnly } = this.state;

    const shouldOpenCalendar = !isOpen && nextState.isOpen;
    const pickOneDateChanged = pickOneDate !== nextProps.pickOneDate;
    const directionChanged = isDirectOnly !== nextState.isDirectOnly;

    const startDateHasChanged = currentDates.startDate !== nextDates.startDate;
    const endDateHasChanged = currentDates.endDate !== nextDates.endDate;
    const dateHasChanged = startDateHasChanged || endDateHasChanged;

    if (shouldOpenCalendar || pickOneDateChanged || dateHasChanged || directionChanged) {
      if (withHeatmap && updateHeatmap) {
        const { startDate = '', endDate = '' } = nextProps.input.value;
        updateHeatmap(!nextProps.pickOneDate, startDate, endDate, nextState.isDirectOnly);
      }
    }
  }

  componentDidMount() {
    // добавлен setTimeout для фикса бага android, когда при инициализации выславляется неверный window.innerWidth
    setTimeout(this.handleResize, 100);
    this.fetchHolidays();

    const container = this.containerRef.current;
    if (this.props.defaultCalendarOpened && container) {
      container.focus();
    }

    window.addEventListener('resize', this.handleResize);
  }

  componentDidUpdate(prevProps, prevState) {
    const { isOpen } = this.state;
    const { isMobileLayout } = this.props;

    if (isOpen && !isMobileLayout) {
      this.bindOutsideClicks();
    } else {
      this.unbindOutsideClicks();
    }
  }

  componentWillUnmount() {
    clearTimeout(this._closeTO);
    this.unbindOutsideClicks();
    window.removeEventListener('resize', this.handleResize);

    if (this.portalRoot) {
      document.body.removeChild(this.portalRoot);
    }
    this.portalRoot = null;
  }

  async fetchHolidays() {
    if (lang !== 'ru') {
      return;
    }

    const { maxDate, minDate } = this.props;
    const fromDate = new Date(minDate).toISOString().split('T')[0];
    const toDate = new Date(maxDate).toISOString().split('T')[0];

    let res;
    try {
      res = await superagent.get(`/_activities/v1/calendar?from=${fromDate}&to=${toDate}`);
    } catch (e) {
      return;
    }

    const holidays = res.body.calendar.reduce((acc, day) => {
      acc[day.date] = day.holiday;

      return acc;
    }, {});

    this.setState({
      holidays: holidays,
    });
  }

  bindOutsideClicks() {
    if (!this._bound) {
      const { isTouchDevice } = this.props;
      const eventName = isTouchDevice ? 'touchend' : 'click';

      document.addEventListener(eventName, this.handleClickOutside);
      this._bound = true;
    }
  }

  unbindOutsideClicks() {
    if (this._bound) {
      const { isTouchDevice } = this.props;
      const eventName = isTouchDevice ? 'touchend' : 'click';

      document.removeEventListener(eventName, this.handleClickOutside);
      this._bound = false;
    }
  }

  setTriggerRef = (node) => {
    this.triggerRef = node;
  };

  getOverlayPosition = () => {
    const { usePortalOverlay } = this.props;
    if (!usePortalOverlay) {
      return {};
    }

    if (!this.triggerRef) {
      return {};
    }

    const inputPosition = this.triggerRef.getBoundingClientRect();
    return {
      top: (window.scrollY || window.pageYOffset) + inputPosition.bottom,
      left: inputPosition.left - OFFSET_FROM_SIDE_BORDER,
      maxHeight: window.innerHeight - inputPosition.bottom - OFFSET_FROM_SCREEN_BOTTOM_BORDER,
    };
  };

  handleResize = () => {
    const { isOpen } = this.state;
    if (isOpen) {
      this.setState({ position: this.getOverlayPosition() });
    }
  };

  handleClose = (e) => {
    this.setState({
      isOpen: false,
      hoveredDate: undefined,
      hoveredTime: undefined,
      value: undefined,
    });

    const { defaultTimeValue, timeField, input, onBlur, onClose } = this.props;

    onBlur(e);
    if (input && input.onBlur) {
      input.onBlur(e);
    }

    if (
      input.value.startDate &&
      defaultTimeValue &&
      timeField &&
      !timeField.input.value &&
      timeField.input.onChange
    ) {
      timeField.input.onChange(defaultTimeValue);
    }

    onClose(e);
  };

  handleOpen = (e) => {
    clearTimeout(this._closeTO);

    const { input, onFocus, onOpen } = this.props;

    if (input && input.onFocus) {
      input.onFocus(e);
    }

    this.setState({
      isOpen: true,
      value: input && input.value,
      position: this.getOverlayPosition(),
    });

    onFocus(e);
    onOpen();
  };

  handleClick = (e) => {
    const { isDisabled, skipMouseDownHandler, isMobileLayout } = this.props;

    if (isMobileLayout || isDisabled || skipMouseDownHandler) {
      return;
    }

    const { isOpen } = this.state;

    const overlayNode = this.overlayRef.current;
    if (overlayNode && overlayNode.contains(e.target)) {
      return;
    }

    if (isOpen && !this._preventCloseByTriggerClick) {
      this.handleCloseAsync(e);
    } else {
      this.handleOpen(e);
    }
  };

  handleDirectionSwitch = () => {
    const { isDirectOnly } = this.state;
    KM('calendar_direct_toggle');

    this.setState({
      isDirectOnly: !isDirectOnly,
    });
  };

  handleFocus = (e) => {
    // кейс: инпут активен > клик по адресной строке > клик по инпуту (focus > click)
    // без этого привента handleClick будет скрывать дропдаун
    this._preventCloseByTriggerClick = true;
    setTimeout(() => (this._preventCloseByTriggerClick = false), 200);

    const { isDisabled } = this.props;

    if (isDisabled) {
      return null;
    }

    this.handleOpen(e);
  };

  // используется для кейсов, когда клик фокусирует элементу, но при этому должен закрывать
  // компонент
  handleCloseAsync = () => {
    this._closeTO = setTimeout(this.handleClose, 30);
  };

  //используется для закрытия календаря при кликах внутри формы поиска в случае передачи параметра preventCloseCalendarOnOutsideClick
  isInsideSearchForm = (element) => {
    while (element) {
      element = element?.parentNode;
      if (element?.nodeName === 'FORM') {
        return true;
      }
    }
    return false;
  };

  handleClickOutside = (e) => {
    const { isMobileLayout, preventCloseCalendarOnOutsideClick } = this.props;
    const containerNode = this.containerRef.current;
    const overlayNode = this.overlayRef.current;
    const element = e.target;

    if (
      (containerNode && containerNode.contains(element)) ||
      (overlayNode && overlayNode.contains(element)) ||
      isMobileLayout
    ) {
      return;
    }

    if (!preventCloseCalendarOnOutsideClick) {
      this.handleCloseAsync();
    }

    if (
      preventCloseCalendarOnOutsideClick &&
      (this.hasSomeValue() || this.isInsideSearchForm(element))
    ) {
      this.handleCloseAsync();
    }
  };

  handleBlur = (e) => {
    const { isMobileLayout } = this.props;
    if (isMobileLayout || !e || !e.relatedTarget) {
      return null;
    }

    this.handleCloseAsync();
  };

  handleDateHover = (date) => {
    this.setState({ hoveredDate: date });
  };

  handleDatesContainerLeave = () => {
    this.setState({ hoveredDate: undefined });
  };

  handleTimeHover = (hoveredTime) => {
    this.setState({
      hoveredTime,
    });
  };

  handleTimeContainerLeave = () => {
    this.setState({ hoveredTime: undefined });
  };

  handleEnter = (e) => {
    const { isOpen } = this.state;

    if (!isOpen) {
      this.handleOpen(e);
    }
  };

  handleKeyDown = (e) => {
    switch (e.key) {
      case 'Enter':
      case ' ':
        this.handleEnter(e);
        break;
      case 'Escape':
        this.handleBlur(e);
        break;
      default:
      // do nothing
    }
  };

  hasSomeValue() {
    const { input } = this.props;

    return input.value && (input.value.startDate || input.value.endDate);
  }

  handleReset = (e) => {
    e.stopPropagation();

    const { onReset } = this.props;
    onReset();
  };

  renderIcon() {
    const { isOpen } = this.state;
    const { withArrowIcon, isResettable } = this.props;

    if (isResettable && this.hasSomeValue()) {
      return <CloserIcon onMouseDown={this.handleReset} />;
    }

    if (withArrowIcon) {
      return <ChevronDownIcon className={cls(styles.arrow, { [styles['arrow-open']]: isOpen })} />;
    }

    return null;
  }

  renderTrigger() {
    const { isOpen } = this.state;

    const {
      TriggerComponent,
      triggerProps,
      input: { name },
      meta,
      isLoading,
      errorPlacement,
      CustomDatesFormatter,
      childrenClassNames,
    } = this.props;

    const isInvalid = meta.submitFailed && meta.invalid;

    return (
      <TriggerComponent
        name={name}
        isActive={isOpen}
        isLoading={isLoading}
        isInvalid={isInvalid}
        hasBottomLine={false}
        hideThrobber={true}
        errorPlacement={errorPlacement}
        className={childrenClassNames.input}
        iconNode={this.renderIcon()}
        parentInputRef={this.setTriggerRef}
        {...triggerProps}
      >
        {CustomDatesFormatter ? (
          cloneElement(<CustomDatesFormatter />, this.getFormatterProps())
        ) : (
          <DatesFormatter {...this.getFormatterProps()} />
        )}
      </TriggerComponent>
    );
  }

  getFormatterProps() {
    const { hoveredDate, hoveredTime } = this.state;

    const {
      input: { value = {} },
      meta,
      timeField,
      placeholder,
      pickOneDate,
      showNights,
      endDatePlaceholder,
      triggerAdditionalInfo,
      forceLongMonth,
      childrenClassNames,
      foldSameDate,
      endDateIsOptional,
      shouldShowTomorrow,
    } = this.props;

    const chosenTime = timeField && timeField.input.value;
    const isInvalid = meta.submitFailed && meta.invalid;

    return {
      startDate: value.startDate,
      endDate: value.endDate,
      chosenTime,
      pickOneDate,
      hoveredDate,
      hoveredTime,
      showNights,
      additionalInfo: triggerAdditionalInfo,
      placeholder,
      endDatePlaceholder,
      isInvalid,
      forceLongMonth,
      childrenClassNames,
      foldSameDate,
      endDateIsOptional,
      shouldShowTomorrow,
    };
  }

  handleCalendarDateSelect = (startDate, endDate) => {
    const { pickOneDate, timeField, isMobileLayout, onDateSelect } = this.props;
    const { value } = this.state;

    const hasValue = value && value.startDate;
    const twoDatesSelected = isDate(startDate) && isDate(endDate);
    const shouldClosed = pickOneDate || twoDatesSelected;

    if (isMobileLayout) {
      if (!hasValue && shouldClosed && !timeField) {
        this.handleCloseAsync();
      }
      const newValue = { startDate, endDate };
      this.setState({ value: newValue });
      this.props.onDateSelect(startDate, endDate);
      return;
    }

    if (shouldClosed && !timeField) {
      this.handleCloseAsync();
    }

    onDateSelect(startDate, endDate);
  };

  handleCloseButtonClick = (e) => {
    const { onDateSelect } = this.props;
    const { value } = this.state;

    this.handleClose();

    if (value && value.startDate) {
      onDateSelect(value.startDate, value.endDate, true);
    }
  };

  handleApplyButtonClick = () => {
    const {
      input: {
        value: { startDate, endDate },
      },
    } = this.props;

    this.handleCloseAsync();

    this.props.onDateSelect(startDate, endDate);
  };

  handleCalendarTimeSelect = (time) => {
    const {
      closeAfterSelect,
      input: {
        value: { startDate },
      },
      onTimeSelect,
    } = this.props;

    if (closeAfterSelect && isDate(startDate)) {
      this.handleCloseAsync();
    }

    onTimeSelect(time);
  };

  renderCalendar() {
    const {
      isMobileSupport, // todo rename
      input: { value = {} },
      pickOneDate,
      endOnlyAfterStart,
      minDate,
      maxDate,
      maxDays,
      minTime,
      maxTime,
      differentDates,
      heatmap,
      heatmapStatus,
      withHeatmap,
      showHeatmapLegend,
      showHeatmapPrices,
      checkIfDateIsDisabled,
      timeField,
      hideMonthsNavigation,
      hideYearInDatesContainer,
      showRouteDirections,
      routeDirectionsOptions,
      defaultOptionDirectionIndex,
      onOptionSelect,
      startDateIsFixed,
      scrollToWeek,
      isMobileLayout,
      maxDaysTooltipText,
    } = this.props;
    const { hoveredDate, isDirectOnly, holidays } = this.state;

    const heatmapProps = withHeatmap
      ? {
          heatmap,
          heatmapStatus,
          isDirectOnly,
          withHeatmap,
          showHeatmapLegend,
          showHeatmapPrices,
          onDirectionSwitch: this.handleDirectionSwitch,
        }
      : {};

    return (
      <Calendar
        startDateIsFixed={startDateIsFixed}
        startDate={value.startDate}
        endDate={value.endDate}
        endOnlyAfterStart={endOnlyAfterStart}
        pickOneDate={pickOneDate}
        hoveredDate={hoveredDate}
        minDate={minDate}
        maxDate={maxDate}
        minTime={minTime}
        maxTime={maxTime}
        maxDays={maxDays}
        maxDaysTooltipText={maxDaysTooltipText}
        holidays={holidays}
        differentDates={differentDates}
        isMobileSupport={isMobileSupport}
        isMobile={isMobileLayout}
        scrollToWeek={scrollToWeek}
        checkIfDateIsDisabled={checkIfDateIsDisabled}
        timeField={pickOneDate ? timeField : undefined}
        onDatesContainerLeave={this.handleDatesContainerLeave}
        onTimeContainerLeave={this.handleTimeContainerLeave}
        onTimeHover={this.handleTimeHover}
        onDateHover={this.handleDateHover}
        onTimeSelect={this.handleCalendarTimeSelect}
        onDateSelect={this.handleCalendarDateSelect}
        hideMonthsNavigation={hideMonthsNavigation || isMobileLayout}
        hideYearInDatesContainer={hideYearInDatesContainer}
        showRouteDirections={showRouteDirections}
        routeDirectionsOptions={routeDirectionsOptions}
        defaultOptionDirectionIndex={defaultOptionDirectionIndex}
        onOptionSelect={onOptionSelect}
        {...heatmapProps}
      />
    );
  }

  renderHeader() {
    const { children, isMobileLayout } = this.props;

    if (!children) {
      return null;
    }

    return (
      <div
        className={cls(styles.children, {
          [styles['children--isMobile']]: isMobileLayout,
        })}
      >
        {children}
      </div>
    );
  }

  renderFooter() {
    const {
      input: { value = {} },
      pickOneDate,
      withHeatmap,
      showHeatmapLegend,
      heatmap,
      heatmapStatus,
      showNights,
      endDateIsOptional,
    } = this.props;
    const { value: stateValue, isDirectOnly } = this.state;

    const hasValue = stateValue && stateValue.startDate;
    const twoDatesSelected = isDate(stateValue.startDate) && isDate(stateValue.endDate);
    const showFooterButton = pickOneDate || endDateIsOptional ? hasValue : twoDatesSelected;

    const showFormattedDates = pickOneDate ? !!stateValue.startDate : !!stateValue.endDate;

    const diffDays =
      !!stateValue.startDate && !!stateValue.endDate
        ? Math.ceil((stateValue.endDate - stateValue.startDate) / (1000 * 60 * 60 * 24))
        : 0;

    const formatterProps = {
      ...this.getFormatterProps(),
      showNights: false,
    };

    return (
      <>
        {withHeatmap && (
          <Heatmap
            heatmap={heatmap}
            heatmapStatus={heatmapStatus}
            isDirectOnly={isDirectOnly}
            onSwitch={this.handleDirectionSwitch}
            showHeatmapLegend={showHeatmapLegend}
          />
        )}
        {showFooterButton && (
          <Button
            className={cls(styles.button, { [styles['button--margin']]: withHeatmap })}
            withFullWidth={true}
            onClick={this.handleApplyButtonClick}
          >
            {l10n('datepicker.select')}{' '}
            {showFormattedDates && <DatesFormatter {...formatterProps} />}
            {showFormattedDates && showNights ? ', ' : ''}
            {showFormattedDates && showNights ? (
              <span className={styles.nightsText}>
                {diffDays}
                <MoonIcon className={styles.moonIcon} />
              </span>
            ) : (
              ''
            )}
          </Button>
        )}
      </>
    );
  }

  renderContent() {
    const { calendarPlacement, childrenClassNames, usePortalOverlay } = this.props;

    const { position } = this.state;

    return (
      <div
        ref={this.overlayRef}
        style={position}
        className={cls(
          styles.calendar,
          styles[`calendar--placement-${calendarPlacement}`],
          childrenClassNames.calendar,
          { [styles['calendar--portal']]: usePortalOverlay }
        )}
        tabIndex="-1"
      >
        {this.renderHeader()}
        {this.renderCalendar()}
      </div>
    );
  }

  renderOverlay() {
    const { isOpen } = this.state;
    const { isMobileLayout, title } = this.props;

    if (!isOpen) {
      return null;
    }

    const { calendarPlacement, overlayProps, usePortalOverlay } = this.props;

    const contentNode = this.renderContent();

    if (isMobileLayout) {
      return (
        <Modal
          isOpen={true}
          fullScreenOnMobile={true}
          fullScreenBreakpoint={Infinity}
          onClose={this.handleCloseButtonClick}
          contentOptions={{
            title,
            className: styles.fullHeight,
            hasFixedHeaderAndFooter: true,
            headerContentNode: this.renderHeader(),
            footerContentNode: this.renderFooter(),
            childrenClassNames: {
              body: styles.body,
              footer: styles.footer,
            },
          }}
        >
          {this.renderCalendar()}
        </Modal>
      );
    }

    if (usePortalOverlay) {
      if (!this.portalRoot) {
        this.portalRoot = document.createElement('div');
        document.body.appendChild(this.portalRoot);
      }

      return createPortal(contentNode, this.portalRoot);
    }

    return (
      <Overlay
        show={true}
        container={this.containerRef.current}
        isAbsolutePosition={true}
        isFullScreen={false}
        placement={calendarPlacement}
        {...overlayProps}
      >
        {contentNode}
      </Overlay>
    );
  }

  render() {
    const { specificLocators, childrenClassNames } = this.props;

    return (
      <div
        ref={this.containerRef}
        className={cls(styles.Datepicker, childrenClassNames.container)}
        onMouseDown={this.handleClick}
        onKeyDown={this.handleKeyDown}
        onFocus={this.handleFocus}
        onBlur={this.handleBlur}
        tabIndex="0"
        role="button"
        data-locator={cls(mainLocator, specificLocators)}
      >
        {this.renderTrigger()}
        {this.renderOverlay()}
      </div>
    );
  }
}

export { DatesFormatter };

export default Datepicker;
